import { FC, useEffect, useRef, useState } from 'react';
import { Close, CloudUpload } from '@mui/icons-material';

import { importLeads, previewLeadImport } from '../../api/leads';
import { LeadImportPreview, LeadImportResult } from '../../data/types';

type LeadImportModalProps = {
  adminEmail: string;
  onClose: () => void;
  onComplete: (message: string) => void;
};

/** Modal for importing HubSpot, Resource Partner, or generic lead spreadsheets. */
export const LeadImportModal: FC<LeadImportModalProps> = ({ adminEmail, onClose, onComplete }) => {
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<LeadImportPreview | null>(null);
  const [result, setResult] = useState<LeadImportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const handleFileChange = async (nextFile: File | null) => {
    setFile(nextFile);
    setPreview(null);
    setResult(null);
    setError('');
    if (!nextFile) return;
    setLoading(true);
    try {
      const previewData = await previewLeadImport(nextFile, adminEmail);
      setPreview(previewData);
    } catch {
      setError('Could not preview this file.');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    setError('');
    try {
      const importResult = await importLeads(file, adminEmail);
      setResult(importResult);
      onComplete(
        `Import complete: ${importResult?.created ?? 0} created, ${importResult?.updated ?? 0} updated, ${importResult?.merged ?? 0} merged, ${importResult?.drafts ?? 0} drafts.`
      );
    } catch {
      setError('Import failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <button type="button" className="absolute inset-0 bg-black/40" onClick={onClose} aria-label="Close" />
      <div className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold font-raleway text-[#013847]">Import leads</h2>
            <p className="text-sm text-[#737373] font-open-sans">
              Upload HubSpot exports, Resource Partner spreadsheets, or generic CSV/XLSX files.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-sm font-semibold font-raleway">
            <Close />
          </button>
        </div>

        <input
          ref={uploadInputRef}
          type="file"
          accept=".csv,.xlsx"
          className="hidden"
          onChange={(event) => handleFileChange(event.target.files?.[0] ?? null)}
        />

        <button
          type="button"
          disabled={loading}
          onClick={() => uploadInputRef.current?.click()}
          className="mb-4 flex w-full flex-col items-center justify-center rounded-xl border border-dashed border-[#CFE8EF] bg-[#EFF8FA]/40 px-6 py-8 text-[#013847]"
        >
          <CloudUpload className="mb-2" />
          <span className="font-semibold font-raleway">{file ? file.name : 'Choose CSV or XLSX file'}</span>
          <span className="mt-1 text-xs text-[#737373] font-open-sans">Email upserts existing leads; business-only rows become drafts.</span>
        </button>

        {preview ? (
          <div className="mb-4 rounded-lg border border-[#E9E9E9] bg-[#FAFAFA] p-4 text-sm font-open-sans">
            <p>
              Detected source: <strong>{preview.source}</strong>
            </p>
            <p>Rows: {preview.total_rows ?? 0} total, {preview.with_email ?? 0} with email, {preview.draft_rows ?? 0} draft rows.</p>
          </div>
        ) : null}

        {result ? (
          <div className="mb-4 rounded-lg border border-[#CFE8EF] bg-[#EFF8FA] p-4 text-sm font-open-sans">
            <p>
              Created {result.created ?? 0}, updated {result.updated ?? 0}, merged {result.merged ?? 0}, drafts{' '}
              {result.drafts ?? 0}, skipped {result.skipped ?? 0}.
            </p>
          </div>
        ) : null}

        {error ? <p className="mb-4 text-sm text-red-600 font-open-sans">{error}</p> : null}

        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="rounded-lg border border-[#E9E9E9] px-4 py-2 text-sm font-semibold font-raleway">
            Close
          </button>
          <button
            type="button"
            disabled={!file || loading || Boolean(result)}
            onClick={handleImport}
            className="rounded-lg bg-[#013847] px-4 py-2 text-sm font-semibold text-white font-raleway disabled:opacity-50"
          >
            {loading ? 'Working…' : 'Import leads'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeadImportModal;
